// =====================================================
// ComparativoInformeProyeccion.js
// Panel de análisis comparativo entre el Informe (condiciones normales)
// y la Proyección Crediticia (condiciones actuales con NDVI)
// Ubicar en: src/pages/Agro/Productor/ComparativoInformeProyeccion.js
// =====================================================
// Agregar en el tab "Resumen" del InformeAgropecuario,
// DESPUÉS de SeccionDictamen y ANTES de los KPIs.
// Props: metricas (del useMemo), informeCompleto (del endpoint)
// =====================================================

import React from "react"
import { Card, CardBody } from "reactstrap"

const fmtUsd = v => {
  if (v == null || isNaN(v)) return "—"
  return (
    "USD " +
    Number(v).toLocaleString("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  )
}

const fmtPct = v => {
  if (v == null || isNaN(v)) return "—"
  return Number(v).toFixed(1) + "%"
}

const PALETA = {
  verde: { bg: "#f0faf4", text: "#1a7431", border: "#34a853" },
  ambar: { bg: "#fef9ee", text: "#92400e", border: "#f59e0b" },
  rojo: { bg: "#fef2f2", text: "#7f1d1d", border: "#b91c1c" },
  azul: { bg: "#eff6ff", text: "#1e40af", border: "#3b82f6" },
  gris: { bg: "#f8fafc", text: "#334155", border: "#94a3b8" },
}

const ComparativoInformeProyeccion = ({ metricas, informeCompleto }) => {
  if (!metricas || !informeCompleto?.proyeccion?.resumen) return null

  const proy = informeCompleto.proyeccion.resumen
  const dict = informeCompleto.proyeccion.dictamen

  // Calcular diferencias
  const filas = [
    {
      concepto: "Ingreso total",
      informe: metricas.ingresoTotal,
      proyeccion: Number(proy.ingresoTotalUsd),
      explicacion: "La proyección ajusta el rendimiento por NDVI satelital",
    },
    {
      concepto: "Costos totales",
      informe: metricas.costoTotal,
      proyeccion: Number(proy.costoTotalUsd),
      invertir: true, // en costos, menor es mejor
      explicacion: "Costos desde las mismas tablas de administración",
    },
    {
      concepto: "Margen neto",
      informe: metricas.margenNetoTotal,
      proyeccion: Number(proy.margenNetoUsd),
      explicacion:
        "La diferencia surge del ajuste de rendimiento y valuación ganadera",
    },
    {
      concepto: "Capacidad de pago",
      informe: metricas.capacidadPago,
      proyeccion: Number(proy.capacidadPagoAnualUsd),
      explicacion: "70% del margen neto en ambos casos",
    },
  ]

  // ¿Hay diferencia significativa?
  const diffMargen = proy.margenNetoUsd - metricas.margenNetoTotal
  const diffPct =
    metricas.margenNetoTotal !== 0
      ? (diffMargen / Math.abs(metricas.margenNetoTotal)) * 100
      : 0
  const hasDiff = Math.abs(diffPct) > 5

  if (!hasDiff) {
    // Si la diferencia es <5%, mostrar solo un badge verde
    return (
      <div className="d-flex align-items-center gap-2 mb-3">
        <span
          className="px-2 py-1 rounded d-flex align-items-center gap-1"
          style={{
            fontSize: "0.72rem",
            backgroundColor: PALETA.verde.bg,
            color: PALETA.verde.text,
            border: `1px solid ${PALETA.verde.border}30`,
          }}
        >
          <i className="mdi mdi-check-circle" style={{ fontSize: "0.8rem" }} />
          Informe y proyección alineados (diferencia menor al 5%)
        </span>
      </div>
    )
  }

  return (
    <Card
      className="mb-3 border-0 overflow-hidden"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
    >
      <div style={{ height: "3px", background: PALETA.azul.border }} />
      <CardBody className="py-3">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 28,
                height: 28,
                backgroundColor: PALETA.azul.bg,
                border: `1.5px solid ${PALETA.azul.border}`,
              }}
            >
              <i
                className="mdi mdi-compare-horizontal"
                style={{ fontSize: "0.8rem", color: PALETA.azul.border }}
              />
            </div>
            <div>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: PALETA.azul.text,
                }}
              >
                Análisis comparativo
              </span>
              <span
                className="text-muted d-block"
                style={{ fontSize: "0.68rem", lineHeight: 1.2 }}
              >
                Informe (condiciones normales) vs Proyección (condiciones
                actuales)
              </span>
            </div>
          </div>
          <span
            className="px-2 py-0 rounded-pill"
            style={{
              fontSize: "0.65rem",
              backgroundColor:
                Math.abs(diffPct) > 20 ? PALETA.ambar.bg : PALETA.gris.bg,
              color:
                Math.abs(diffPct) > 20 ? PALETA.ambar.text : PALETA.gris.text,
              border: `1px solid ${Math.abs(diffPct) > 20 ? PALETA.ambar.border : PALETA.gris.border}30`,
            }}
          >
            Diferencia margen: {diffPct > 0 ? "+" : ""}
            {fmtPct(diffPct)}
          </span>
        </div>

        {/* Tabla comparativa */}
        <div
          className="rounded overflow-hidden"
          style={{ border: `1px solid #e2e8f0` }}
        >
          {/* Header de tabla */}
          <div
            className="d-flex"
            style={{
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              fontSize: "0.68rem",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            <div style={{ flex: 2, padding: "6px 12px" }}>Concepto</div>
            <div style={{ flex: 2, padding: "6px 12px", textAlign: "right" }}>
              <i className="mdi mdi-file-document-outline me-1" />
              Informe
            </div>
            <div style={{ flex: 2, padding: "6px 12px", textAlign: "right" }}>
              <i className="mdi mdi-satellite-variant me-1" />
              Proyección
            </div>
            <div style={{ flex: 1, padding: "6px 12px", textAlign: "center" }}>
              Dif.
            </div>
          </div>

          {/* Filas */}
          {filas.map((f, i) => {
            const diff = f.proyeccion - f.informe
            const pctDiff =
              f.informe !== 0 ? (diff / Math.abs(f.informe)) * 100 : 0
            const isPositive = f.invertir ? diff < 0 : diff > 0
            const isSignificant = Math.abs(pctDiff) > 5

            return (
              <div
                key={i}
                className="d-flex align-items-center"
                style={{
                  borderBottom:
                    i < filas.length - 1 ? "1px solid #f1f5f9" : "none",
                  backgroundColor: i === filas.length - 2 ? "#fafbfc" : "white", // resaltar margen
                }}
              >
                <div style={{ flex: 2, padding: "8px 12px" }}>
                  <span
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: f.concepto === "Margen neto" ? 600 : 400,
                      color: "#1f2937",
                    }}
                  >
                    {f.concepto}
                  </span>
                </div>
                <div
                  style={{
                    flex: 2,
                    padding: "8px 12px",
                    textAlign: "right",
                    fontSize: "0.82rem",
                    color: "#334155",
                  }}
                >
                  {fmtUsd(f.informe)}
                </div>
                <div
                  style={{
                    flex: 2,
                    padding: "8px 12px",
                    textAlign: "right",
                    fontSize: "0.82rem",
                    color: "#334155",
                  }}
                >
                  {fmtUsd(f.proyeccion)}
                </div>
                <div
                  style={{ flex: 1, padding: "8px 12px", textAlign: "center" }}
                >
                  {isSignificant ? (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: isPositive
                          ? PALETA.verde.text
                          : PALETA.ambar.text,
                      }}
                    >
                      {pctDiff > 0 ? "+" : ""}
                      {fmtPct(pctDiff)}
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                      ≈
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Explicación */}
        <div
          className="mt-2 rounded px-3 py-2"
          style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <div className="d-flex align-items-start gap-2">
            <i
              className="mdi mdi-information-outline"
              style={{
                fontSize: "0.85rem",
                color: PALETA.azul.border,
                marginTop: 1,
              }}
            />
            <div
              style={{ fontSize: "0.75rem", color: "#475569", lineHeight: 1.5 }}
            >
              <strong style={{ color: "#1e293b" }}>¿Por qué difieren?</strong>
              <br />
              El <strong>informe</strong> calcula con rendimientos promedio de
              la zona (condiciones normales). La <strong>proyección</strong>{" "}
              ajusta esos rendimientos según el estado real del cultivo medido
              por satélite (NDVI)
              {dict?.observaciones?.some(o => o.includes("NDVI")) ? (
                <span>
                  {" "}
                  — actualmente hay cultivos con condición baja que reducen el
                  rendimiento esperado
                </span>
              ) : null}
              .
              {dict?.observaciones?.some(o => o.includes("NDVI")) && (
                <span>
                  {" "}
                  La ganadería se valúa por categoría real del stock en la
                  proyección.
                </span>
              )}
              <br />
              <span style={{ color: "#94a3b8" }}>
                Ambos usan las mismas tablas de precios y costos
                (Administración).
              </span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default ComparativoInformeProyeccion
